import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MdCheckCircle } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { sendAnalytics } from '../../../analytics';
import { CustomButton, CustomFormInput } from '../../atoms';
import { postNotifyAuthAuthorize, postNotifyCustomerMobileOtp } from '../../../reduxtoolkit/slices/gq-notification';
import { postCustomerMobileAuthNumber, postCustomerMobileAuthOtp } from '../../../reduxtoolkit/slices/gq-customer';
import { ANALYTICS_EVENTS } from '../../../utils/constants';
import { hasDollarInString, isSuccessStatus, showErrorToast } from '../../../utils/utils';
import { localStorageUtil } from '../../../utils/localStorageUtil';
import { PLATFORM_CODE } from '../../../../constants';
import classes from './CustomFormMobile.module.scss';

const CustomFormMobile = (props) => {
    const {
        mobileInput,
        studentDetails,
        isOtpVerificationEnabled,
        formik,
        isOtpVerificationSuccess,
        setIsOtpVerificationSuccess,
        maskedNumberString,
        formBlock,
        formBlockData,
        isMultiVerEnabled,
        handleChangeVerificationMethod,
        isDisabled,
        // Primary identifier props
        isPrimaryIdentifierFlow,
        primaryIdentifierValue,
    } = props;

    if (!mobileInput) return null;

    const dispatch = useDispatch();

    const [isOtpSent, setIsOtpSent] = useState(false);
    const [isSendOtpLoading, setIsSendOtpLoading] = useState(false);
    const [isVerifyOtpLoading, setIsVerifyOtpLoading] = useState(false);
    const [otpFieldError, setOtpFieldError] = useState('');
    const [otpFieldValue, setOtpFieldValue] = useState('');

    // 1. Check Customer Number
    const handleCheckCustomerNumber = async (payload) => {
        try {
            await dispatch(postCustomerMobileAuthNumber(payload)).unwrap();
        } catch (error) {
            console.error(error);
        }
    };

    // 2. Generate Notification Auth
    const handleGenerateNotiAuth = async () => {
        try {
            const postNotifyAuthAuthorizeRes = await dispatch(
                postNotifyAuthAuthorize({ platform_code: PLATFORM_CODE }),
            ).unwrap();
            if (isSuccessStatus(postNotifyAuthAuthorizeRes)) {
                localStorageUtil.setItem('gqnotify_accesstoken', postNotifyAuthAuthorizeRes?.data?.access_token);
                localStorageUtil.setItem('gqnotify_refreshtoken', postNotifyAuthAuthorizeRes?.data?.refresh_token);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // 3. Send OTP to mobile number
    const handleSendOtp = async (payload) => {
        try {
            await dispatch(postNotifyCustomerMobileOtp({ mobile: parseInt(payload?.mobile_number) })).unwrap();
            setIsOtpSent(true);
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_SEND_OTP,
                    trackingData: {
                        form_name: formBlock?.name,
                    },
                },
            });
        } catch (error) {
            console.error(error);
            showErrorToast('Unable to send OTP, please contact support@grayquest.com');
        }
    };

    // 4. Verify OTP
    const handleVerifyOtp = async (payload) => {
        try {
            setIsVerifyOtpLoading(true);
            const postCustomerMobileAuthOtpRes = await dispatch(postCustomerMobileAuthOtp(payload)).unwrap();
            if (isSuccessStatus(postCustomerMobileAuthOtpRes)) {
                setIsOtpVerificationSuccess(true);
                if(studentDetails?.cust_bd?.email) {
                    formik.setFieldValue('customer_email', studentDetails?.cust_bd?.email);
                }
                sendAnalytics({
                    vendor: ['mixpanel', 'ga'],
                    eventData: {
                        event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_VERIFY_OTP_SUCCESS,
                        trackingData: {
                            form_name: formBlock?.name,
                        },
                    },
                });
            } else {
                setIsVerifyOtpLoading(false);
            }
        } catch (error) {
            console.error(error);
            if (error?.response?.data?.message) {
                showErrorToast(error?.response?.data?.message);
            } else {
                showErrorToast('Unable to verify OTP, please contact support@grayquest.com');
            }
            setIsVerifyOtpLoading(false);
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_CUSTOM_FORM_VERIFY_OTP_FAIL,
                    trackingData: {
                        form_name: formBlock?.name,
                    },
                },
            });
        }
    };

    const handleSendOtpClick = async () => {
        // Get mobile number from primary identifier or customer_mobile field
        const mobileNumber = isPrimaryIdentifierFlow ? 
            primaryIdentifierValue : 
            formik?.values?.customer_mobile;

        if (
            isOtpVerificationEnabled &&
            studentDetails?.customer_mobile &&
            !hasDollarInString(studentDetails?.customer_mobile) &&
            mobileNumber !== studentDetails?.customer_mobile &&
            !isPrimaryIdentifierFlow
        ) {
            await formik?.setFieldTouched('customer_mobile', true);
            await formik?.setFieldError('customer_mobile', 'Mobile number does not match');
            return;
        }

        // For primary identifier flow, use the primary identifier value
        if (isPrimaryIdentifierFlow) {
            if (!mobileNumber || mobileNumber?.length !== 10) {
                return;
            }
        } else {
            if (
                !(
                    !formik.errors.customer_mobile &&
                    formik?.values?.customer_mobile &&
                    formik?.values?.customer_mobile?.length === 10
                )
            ) {
                formik.setFieldTouched('customer_mobile', true);
                return;
            }
        }

        setIsSendOtpLoading(true);
        await handleCheckCustomerNumber({
            mobile_number: mobileNumber,
        });
        await handleSendOtp({
            mobile_number: mobileNumber,
        });
        setIsSendOtpLoading(false);
    };

    const handleVerifyOtpClick = async () => {
        setOtpFieldError('');
        if (!otpFieldValue || otpFieldValue?.length < 4) {
            setOtpFieldError('Please enter 4 digit OTP');
            return;
        }

        let isNum = /^\d+$/.test(otpFieldValue);
        if (!isNum) {
            setOtpFieldError('OTP must contain only numbers');
            return;
        }

        // Get mobile number from primary identifier or customer_mobile field
        const mobileNumber = isPrimaryIdentifierFlow ? 
            primaryIdentifierValue : 
            formik?.values?.customer_mobile;

        await handleVerifyOtp({
            mobile_number: mobileNumber,
            otp: otpFieldValue,
        });
    };

    const handleChangeVerfMethod = () => {
        handleChangeVerificationMethod('email_verf');
    };

    const handleOtpInputChange = (e) => {
        setOtpFieldError('');
        setOtpFieldValue(e?.target?.value || '');
    };

    useEffect(() => {
        if (isOtpVerificationEnabled) {
            handleGenerateNotiAuth();
        }
        
        // Auto-trigger OTP for primary identifier flow
        if (isPrimaryIdentifierFlow && isOtpVerificationEnabled && !isOtpSent && !isOtpVerificationSuccess) {
            // Check if we should block unverified students
            const blockUnverifiedStudent = formBlockData?.form_config?.block_unverified_student ?? false;
            const isActiveStudent = studentDetails?.stud_bd?.status === 'ACTIVE' || false;
            
            // If block unverified student is enabled, only trigger OTP for verified students
            if (blockUnverifiedStudent) {
                if (studentDetails && isActiveStudent) {
                    handleSendOtpClick();
                }
            } else {
                // If not blocking unverified students, trigger OTP
                handleSendOtpClick();
            }
        }
    }, [isPrimaryIdentifierFlow, isOtpVerificationEnabled, isOtpSent, isOtpVerificationSuccess, studentDetails, formBlockData]);

    return (
        <>
            {/* Only render input field if NOT in primary identifier flow */}
            {!isPrimaryIdentifierFlow && (
                <div className="row">
                    <div className="col-md-12">
                        <div className={classes.MobileInputWrap}>
                            <CustomFormInput
                                inputData={mobileInput || null}
                                formik={formik}
                                key="mobileInput"
                                isDisabled={(isOtpVerificationEnabled && isOtpSent) || isDisabled}
                                maxLength={10}
                                helperText={maskedNumberString}
                            />
                            {isMultiVerEnabled && !isOtpVerificationSuccess && (
                                <button
                                    type="button"
                                    onClick={handleChangeVerfMethod}
                                    className={classes.VerToggleButton}
                                    title="Click here to use email id for verification"
                                >
                                    <input type="checkbox" onChange={handleChangeVerfMethod} />
                                    <span>Want to verify via Email?</span>
                                </button>
                            )}

                            {isOtpVerificationSuccess ? (
                                <>
                                    <span className={classes.Text}>
                                        <span className={classes.Icon}>
                                            <MdCheckCircle fontSize="1.25rem" />
                                        </span>
                                        Mobile Number verified successfully!
                                    </span>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* Show success message for primary identifier flow */}
            {isPrimaryIdentifierFlow && isOtpVerificationSuccess && (
                <div className="row">
                    <div className="col-md-12">
                        <div className={classes.MobileInputWrap}>
                            <span className={classes.Text}>
                                <span className={classes.Icon}>
                                    <MdCheckCircle fontSize="1.25rem" />
                                </span>
                                Mobile Number verified successfully!
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {isOtpVerificationEnabled && !isOtpVerificationSuccess ? (
                <>
                    {isOtpSent ? (
                        <div className="row">
                            <div className="col-md-12">
                                <div className={classes.OtpInputWrap}>
                                    <label htmlFor="customer_mobile_otp">Enter OTP</label>
                                    <input
                                        type="text"
                                        className={classes.OtpInput}
                                        placeholder="Enter OTP"
                                        id="customer_mobile_otp"
                                        name="customer_mobile_otp"
                                        onChange={handleOtpInputChange}
                                        disabled={!isOtpSent || isVerifyOtpLoading}
                                        maxLength={4}
                                    />
                                    {otpFieldError ? <span className="form-error">{otpFieldError}</span> : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {!isOtpSent ? (
                        <div className="row">
                            <div className="col-md-12">
                                <div className={classes.OtpButtonWrap}>
                                    <CustomButton
                                        type="button"
                                        text="Send OTP"
                                        variant="outline"
                                        className={classes.PaymentForm__Submit}
                                        onClick={handleSendOtpClick}
                                        isLoading={isSendOtpLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {isOtpSent ? (
                        <div className="row">
                            <div className="col-md-12">
                                <div className={classes.OtpButtonWrap}>
                                    <CustomButton
                                        type="button"
                                        text="Verify OTP"
                                        className={classes.PaymentForm__Submit}
                                        onClick={handleVerifyOtpClick}
                                        isLoading={isVerifyOtpLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}
                </>
            ) : null}
        </>
    );
};

CustomFormMobile.propTypes = {
    mobileInput: PropTypes.object,
    studentDetails: PropTypes.object,
    isOtpVerificationEnabled: PropTypes.bool,
    formik: PropTypes.object,
    isOtpVerificationSuccess: PropTypes.bool,
    setIsOtpVerificationSuccess: PropTypes.func,
    maskedNumberString: PropTypes.string,
    formBlock: PropTypes.object,
    formBlockData: PropTypes.object,
    isMultiVerEnabled: PropTypes.bool,
    handleChangeVerificationMethod: PropTypes.func,
    isDisabled: PropTypes.bool,
    isPrimaryIdentifierFlow: PropTypes.bool,
    primaryIdentifierValue: PropTypes.string,
};

export default CustomFormMobile;
